import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Badge, Card, Group, Select, Text } from "@mantine/core"
import { formatClock } from "../../../lib/format-date"
import { FinalizationProgress } from "../../FinalizationProgress"
import { FinishConfirmModal } from "../FinishConfirmModal"
import { HealthIndicator } from "../HealthIndicator"
import { LiveTranscriptPanel } from "../LiveTranscriptPanel"
import { MicLevelMeter } from "../MicLevelMeter"
import { ProgressiveSummaryPanel } from "../ProgressiveSummaryPanel"
import { RecordingActionBar } from "../RecordingActionBar"
import { StreamingControlsPanel } from "../StreamingControlsPanel"
import type { IProps } from "./interfaces"

/**
 * Legacy / Voxcanon-flavoured recording card. Composes every recording-phase
 * sub-component (mic meter, action bar, streaming controls, transcript,
 * progressive summary) inside a single bordered Mantine Card.
 *
 * This view is rendered when:
 *   - product is voxcanon, OR
 *   - product is lexicanon AND a recovered recording snapshot is in flight
 *     (the focus mode doesn't have an opinionated layout for that case yet).
 *
 * Pure: every piece of state arrives via props; every interaction is emitted
 * via the `handlers` bag. The smart wrapper owns lifecycle, network IO, and
 * IDB writes.
 */
export const LegacyRecordingPanel = ({
  state,
  mode,
  meetingLabel,
  product,
  isOnline,
  idbWarning,
  uploading,
  uploadCount,
  health,
  wsReconnecting,
  streamingAvailable,
  streamingAdapterList,
  selectedBackend,
  defaultAdapterName,
  languages,
  identifiedSpeakers,
  processingStage,
  micAlert,
  elapsed,
  transcriptStaleSec,
  segments,
  interimText,
  interimSpeaker,
  speakerHypotheses,
  hypothesisPending,
  onHypothesisAction,
  showTranscript,
  progressiveSummary,
  testRecording,
  testCountdown,
  testAudioUrl,
  testAudioClassName,
  finishConfirmOpen,
  error,
  audio,
  handlers,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const isActive = state === "recording" || state === "paused"
  const isFinalizing = state === "finalizing"
  const showFinalizationProgress = isFinalizing || processingStage !== null

  const headerLabel =
    state === "finalizing"
      ? i18n._(msg`Finalizing ${meetingLabel}`)
      : state === "paused"
        ? i18n._(msg`Paused`)
        : i18n._(msg`Recording`)

  const staleAlertMessage =
    segments.length === 0 && languages.length > 0
      ? i18n._(msg`No speech detected yet. Is the language correct? (Currently: ${languages.join(", ")})`)
      : transcriptStaleSec >= 60
        ? i18n._(msg`Live transcription disconnected. Your recording is continuing safely.`)
        : i18n._(msg`Live transcription paused — your recording continues normally.`)

  return (
    <Card data-testid="recording-controls" withBorder padding="lg" mb="md">
      <Group justify="space-between" mb="sm">
        <Text fw={600}>{headerLabel}</Text>
        {(isActive || isFinalizing) && (
          <Text data-testid="recording-timer" ff="monospace" fw={700} opacity={state === "paused" ? 0.5 : 1}>
            {formatClock(elapsed)}
          </Text>
        )}
      </Group>

      {/* Offline badge — shown whenever network is unavailable */}
      {!isOnline && (state === "recording" || state === "paused") && (
        <Alert color="warning" variant="light" mb="xs" py="xs">
          <Text size="xs">
            <Trans>Recording locally — will sync when you reconnect.</Trans>
          </Text>
        </Alert>
      )}

      {(state === "recording" || state === "paused") && <HealthIndicator {...health} />}

      {idbWarning && isActive && (
        <Alert color="warning" variant="light" mb="sm" title={i18n._(msg`Local backup unavailable`)}>
          <Text size="xs">
            Audio backup to local storage is failing. Recording continues, but recovery may be limited if this tab
            crashes. Server upload is your primary backup.
          </Text>
        </Alert>
      )}

      <StreamingControlsPanel
        state={state}
        mode={mode}
        streamingAvailable={streamingAvailable}
        wsReconnecting={wsReconnecting}
        languages={languages}
        streamingAdapterList={streamingAdapterList}
        selectedBackend={selectedBackend}
        defaultAdapterName={defaultAdapterName}
        onModeChange={handlers.onModeChange}
        onLanguagesChange={handlers.onLanguagesChange}
        onBackendChange={handlers.onBackendChange}
      />

      <Select
        label={i18n._(msg`Microphone`)}
        data={audio.devices.map((d) => ({ value: d.deviceId, label: d.label || `Mic ${d.deviceId.slice(0, 8)}` }))}
        value={audio.selectedDevice}
        onChange={audio.setSelectedDevice}
        disabled={isActive}
        size="sm"
        mb="sm"
      />

      {!isFinalizing && (
        <>
          <MicLevelMeter
            state={state}
            volume={audio.volume}
            boostedVolume={audio.boostedVolume}
            boostEnabled={audio.boostEnabled}
            micAlert={micAlert}
            onBoostToggle={audio.onBoostToggle}
          />

          <RecordingActionBar
            state={state}
            monitoring={audio.monitoring}
            selectedDevice={audio.selectedDevice}
            testRecording={testRecording}
            testCountdown={testCountdown}
            testAudioUrl={testAudioUrl}
            product={product}
            meetingLabel={meetingLabel}
            testAudioClassName={testAudioClassName}
            onEnableMic={() => audio.selectedDevice && audio.startMonitoring(audio.selectedDevice)}
            onStartTestMic={handlers.onStartTestMic}
            onStart={handlers.onStart}
            onPause={handlers.onPause}
            onResume={handlers.onResume}
            onRequestFinish={handlers.onRequestFinish}
            onOpenSceneInsights={handlers.onOpenSceneInsights}
          />
        </>
      )}

      {showFinalizationProgress && <FinalizationProgress stage={processingStage} />}

      {identifiedSpeakers.length > 0 && (isActive || isFinalizing) && (
        <Group gap={4} mb="xs">
          <Text size="xs" c="dimmed">
            <Trans>Identified:</Trans>
          </Text>
          {identifiedSpeakers.map((s) => (
            <Badge key={s} size="xs" variant="light">
              {s}
            </Badge>
          ))}
        </Group>
      )}

      {uploading && !processingStage && (
        <Text size="xs" c="dimmed">
          <Trans>Processing audio...</Trans>
        </Text>
      )}
      {uploadCount > 0 && !uploading && !processingStage && (
        <Text size="xs" c="dimmed">
          <Trans>
            {uploadCount} update{uploadCount !== 1 ? "s" : ""} processed
          </Trans>
        </Text>
      )}

      {transcriptStaleSec >= 30 && isActive && mode === "streaming" && (
        <Alert color="warning" variant="light" mt="xs">
          <Text size="xs">{staleAlertMessage}</Text>
        </Alert>
      )}

      {mode === "streaming" && (isActive || isFinalizing) && segments.length > 0 && (
        <LiveTranscriptPanel
          segments={segments}
          interimText={interimText}
          interimSpeaker={interimSpeaker}
          hypotheses={[...speakerHypotheses.values()]}
          pending={hypothesisPending}
          onAction={onHypothesisAction}
          showTranscript={showTranscript}
          onToggleVisible={handlers.onToggleTranscriptVisible}
        />
      )}

      {mode === "streaming" && (isActive || isFinalizing) && (
        <ProgressiveSummaryPanel progressiveSummary={progressiveSummary} />
      )}

      {error && (
        <Alert color="danger" variant="light" mt="xs">
          {error}
        </Alert>
      )}

      <FinishConfirmModal
        opened={finishConfirmOpen}
        meetingLabel={meetingLabel}
        onClose={handlers.onCancelFinish}
        onConfirm={handlers.onConfirmFinish}
      />
    </Card>
  )
}
